import { usePaymentInit } from './usePaymentInit';
import { useHyperswitchRail } from './useHyperswitchRail';
import { useHyperswitchCheckout } from './useHyperswitchCheckout';
import { ROUND_PROVIDER, resolveEffectivePlanId } from '../shared/utils/roundPayments';
import { createPriceRefresh } from '../utils/priceRefresh';
import { useCheckoutModal } from './checkoutModal';
import {
  PAYMENT_INIT_ACTION,
  PAYMENT_SCRIPT_TIMEOUT_MS,
} from '../common/constants/paymentInit';
import { PaymentContractError } from '../utils/paymentContractError';
import { dropUnusableChargebeeSdk, startChargebeeSdk } from '../utils/chargebeeSdk';

interface UseCheckoutSdkOptions {
  planCode: () => string;
  roundedPrice: () => number;
  discPrice: () => number;
  upsale: () => boolean;
}

export function useCheckoutSdk(options: UseCheckoutSdkOptions) {
  const { CHARGEBEE_DOMAIN, CHARGEBEE_KEY } = useRuntimeConfig().public;
  const { $logger } = useNuxtApp();
  const gatewayStore = useKegelGatewayStore();
  const API = useKegelApi();
  const { applyRoundPlanFallback } = useRoundPlanFallback();
  const isHyperswitch = useHyperswitchRail();
  const { isVisible: checkoutIsOpen } = useCheckoutModal();
  const dialogStore = useDialogStore();
  const { initHyperswitchSdk } = useHyperswitchCheckout();
  const { rememberIntent } = useHyperswitchRedirectReturn();
  const intentCache = usePaymentIntentCache('round');

  const chargebeeCredentials = { site: CHARGEBEE_DOMAIN, publishableKey: CHARGEBEE_KEY };
  const startedProvider = ref<string | null>(null);
  const lastLagoIntent = ref<Record<string, any> | null>(null);
  const lastLagoIntentAmount = ref<number | null>(null);
  const hasAskedForLagoIntent = ref(false);
  const failedLagoIntentAmount = ref<number | null>(null);
  const lagoPaymentIntent = computed(() => (
    lastLagoIntentAmount.value === options.roundedPrice() ? lastLagoIntent.value : null
  ));

  async function startSdk(isCurrent: () => boolean): Promise<void> {
    if (!gatewayStore.providerResolved) {
      await API.getPaymentConfig().catch((error: unknown) => $logger.error(error, {
        tags: { flow: 'api', step: 'init_config' },
      }));
    }

    if (!isCurrent()) return;

    if (!options.upsale()) {
      applyRoundPlanFallback(options.planCode());
    }

    startedProvider.value = gatewayStore.provider;
    gatewayStore.railLocked = true;

    if (gatewayStore.provider !== ROUND_PROVIDER) return startChargebeeSdk(chargebeeCredentials, isCurrent);

    await API.ensureLagoCredentials();

    if (!isCurrent()) return;

    return initHyperswitchSdk(ensureLagoIntent, isCurrent);
  }

  const { status, isOffline, load } = usePaymentInit({
    action: PAYMENT_INIT_ACTION.SDK,
    blocksCheckout: true,
    attemptTimeoutMs: PAYMENT_SCRIPT_TIMEOUT_MS,
    requestKey: () => gatewayStore.provider,
    beforeRun: dropUnusableChargebeeSdk,
    initialize: startSdk,
  });

  const needsLagoIntent = computed(() => (
    status.value === 'ready' && hasAskedForLagoIntent.value && !lagoPaymentIntent.value
  ));
  const hasLagoIntentFailed = computed(() => (
    needsLagoIntent.value && failedLagoIntentAmount.value === options.roundedPrice()
  ));

  if (options.upsale()) {
    let isStartedByModal = false;

    watch(checkoutIsOpen, (visible) => {
      if (!visible || isStartedByModal) return;
      isStartedByModal = true;
      void load();
    }, { immediate: true });
  }

  watch(() => gatewayStore.provider, (provider) => {
    if (startedProvider.value && provider !== startedProvider.value) void load();
  });

  let dropCount = 0;

  function dropLagoIntent(): void {
    if (lastLagoIntentAmount.value !== null) intentCache.drop(lastLagoIntentAmount.value);

    lastLagoIntent.value = null;
    lastLagoIntentAmount.value = null;
    dropCount += 1;
  }

  async function ensureLagoIntent(name?: string): Promise<Record<string, any> | null> {
    const amount = options.roundedPrice();
    const dropCountAtStart = dropCount;
    const isStillWanted = () => amount === options.roundedPrice() && dropCount === dropCountAtStart;
    hasAskedForLagoIntent.value = true;
    failedLagoIntentAmount.value = null;

    const intent = await intentCache.ensure(amount, async () => {
      const response = await API.creditCardInit(amount, undefined, name);
      const created = response?.payment_intent ?? null;

      if (!created?.client_secret) {
        throw new PaymentContractError(`Lago payment intent has no client_secret (intent_id: ${created?.id ?? 'none'})`);
      }

      return created;
    }).catch((error: unknown) => {
      if (isStillWanted()) failedLagoIntentAmount.value = amount;
      throw error;
    });

    if (!isStillWanted()) return intent;

    lastLagoIntent.value = intent;
    lastLagoIntentAmount.value = amount;

    if (isHyperswitch.value) {
      rememberIntent(intent.id, {
        item: resolveEffectivePlanId(gatewayStore.provider, options.planCode()),
        planCode: options.planCode(),
        amount,
        discPrice: options.discPrice(),
        upsale: options.upsale(),
      });
    }

    return intent;
  }

  function refreshLagoIntent(): void {
    if (dialogStore.loader) return;

    void ensureLagoIntent().catch(() => undefined);
  }

  const priceRefresh = createPriceRefresh({ refresh: refreshLagoIntent });

  watch([() => options.roundedPrice(), needsLagoIntent], () => {
    if (needsLagoIntent.value) priceRefresh.priceChanged(checkoutIsOpen.value);
  });

  watch(checkoutIsOpen, (open) => {
    if (!open) return;

    priceRefresh.checkoutOpened();
    if (needsLagoIntent.value) refreshLagoIntent();
  });

  onScopeDispose(() => priceRefresh.cancel());

  return {
    status,
    isOffline,
    load,
    isHyperswitch,
    lagoPaymentIntent,
    hasLagoIntentFailed,
    ensureLagoIntent,
    refreshLagoIntent,
    dropLagoIntent,
  };
}
